// frontend/src/pages/owner/TenantEdit.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, CardHeader, Divider, Grid, Stack, TextField,
  Typography, Alert, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent,
  DialogActions
} from '@mui/material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import http from '../../services/http';
import OwnerBrandFooter from '../../components/owner/OwnerBrandFooter';

function parseJSON(value, fallback) {
  try {
    if (value === '' || value == null) return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

export default function TenantEdit() {
  const { slug: paramSlug } = useParams(); // when editing: /owner/tenants/:slug
  const nav = useNavigate();

  const isCreate = !paramSlug || paramSlug === 'new';

  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Basic
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [dept, setDept] = useState('Department of Community Medicine & Family Medicine');

  // Theme
  const [primary, setPrimary] = useState('#004AAD');
  const [accent, setAccent] = useState('#00B4D8');
  const [headerText, setHeaderText] = useState('Department of Community Medicine & Family Medicine');
  const [darkModeDefault, setDarkModeDefault] = useState(true);

  // Features
  const [competencyApproval, setCompetencyApproval] = useState(true);
  const [returnedEdit, setReturnedEdit] = useState(true);

  // Modules (JSON editor)
  const [modulesText, setModulesText] = useState(''); // editable JSON string
  const modulesObj = useMemo(() => parseJSON(modulesText, {}), [modulesText]);
  const modulesValid = useMemo(() => {
    try { JSON.parse(modulesText || '{}'); return true; } catch { return false; }
  }, [modulesText]);

  // Admin dialog
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminMobile, setAdminMobile] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminBusy, setAdminBusy] = useState(false);
  const canCreateAdmin = !isCreate && !!paramSlug;

  // Clone dashboards dialog
  const [cloneOpen, setCloneOpen] = useState(false);
  const [fromSlug, setFromSlug] = useState('');
  const [cloneBusy, setCloneBusy] = useState(false);

  // Load existing tenant when editing
  useEffect(() => {
    let alive = true;
    if (isCreate) return;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await http.get(`/api/owner/tenants/${encodeURIComponent(paramSlug)}`);
        const t = data?.tenant;
        if (!t) throw new Error('Tenant not found');

        if (!alive) return;
        setSlug(t.slug || '');
        setName(t.name || '');
        setDept(t.dept || 'Department of Community Medicine & Family Medicine');

        const theme = t.theme || {};
        setPrimary(theme.primary || '#004AAD');
        setAccent(theme.accent || '#00B4D8');
        setHeaderText(theme.headerText || (t.dept || 'Department of Community Medicine & Family Medicine'));
        setDarkModeDefault(Boolean(theme.darkModeDefault));

        const features = t.features || {};
        setCompetencyApproval(features.competencyApproval !== false);
        setReturnedEdit(features.returnedEdit !== false);

        setModulesText(JSON.stringify(t.modules || {}, null, 2));
      } catch (e) {
        setError(e?.response?.data?.message || e?.message || 'Failed to load tenant');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isCreate, paramSlug]);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      if (!slug || !name) {
        throw new Error('Slug and Name are required');
      }
      if (!modulesValid) {
        throw new Error('Modules must be valid JSON');
      }

      const payload = {
        slug: slug.trim(),
        name: name.trim(),
        dept: dept.trim() || undefined,
        theme: {
          primary: primary || '#004AAD',
          accent: accent || '#00B4D8',
          headerText: headerText || dept || 'Department of Community Medicine & Family Medicine',
          darkModeDefault: Boolean(darkModeDefault),
        },
        features: {
          competencyApproval: Boolean(competencyApproval),
          returnedEdit: Boolean(returnedEdit),
        },
        modules: modulesObj,
      };

      // Use upsert-style POST for both create and edit (controller supports idempotent upsert by slug)
      await http.post('/api/owner/tenants', payload);

      // After save, go back to list or to this tenant’s detail page
      nav('/owner/tenants', { replace: true });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateAdmin() {
    setAdminBusy(true);
    try {
      if (!adminPassword || !(adminEmail || adminMobile)) {
        throw new Error('Email or Mobile and Password are required');
      }
      await http.post(`/api/owner/tenants/${encodeURIComponent(paramSlug)}/admin`, {
        name: adminName || 'Dept Admin',
        email: adminEmail || undefined,
        mobile: adminMobile || undefined,
        password: adminPassword,
      });
      setAdminOpen(false);
      setAdminName('');
      setAdminEmail('');
      setAdminMobile('');
      setAdminPassword('');
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || 'Failed to create admin');
    } finally {
      setAdminBusy(false);
    }
  }

  async function handleClone() {
    setCloneBusy(true);
    try {
      await http.post(`/api/owner/tenants/${encodeURIComponent(paramSlug)}/clone`, {
        fromSlug: (fromSlug || '').trim() || undefined,
      });
      setCloneOpen(false);
      setFromSlug('');
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || 'Clone/seed failed');
    } finally {
      setCloneBusy(false);
    }
  }

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">{isCreate ? 'New Institutional Portal' : `Edit Institutional Portal: ${paramSlug}`}</Typography>
        <Stack direction="row" spacing={1}>
          <Button component={RouterLink} to="/owner/tenants" variant="outlined">Back</Button>
          {!isCreate && (
            <>
              <Button variant="outlined" onClick={() => setCloneOpen(true)}>Clone Configuration</Button>
              <Button variant="outlined" onClick={() => setAdminOpen(true)}>Create Admin</Button>
              <Button
                variant="contained"
                href={`/t/${encodeURIComponent(paramSlug)}/cmfm-login`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Portal
              </Button>
            </>
          )}
          <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Grid container spacing={2}>
        {/* Basic */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Institution" />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                <TextField
                  label="Portal identifier (slug)"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                  helperText={isCreate ? 'Unique technical identifier used in /t/:slug/ routes' : 'Portal identifier cannot be changed on edit'}
                  required
                  disabled={!isCreate || loading}
                />
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={loading}
                />
                <TextField
                  label="Department"
                  value={dept}
                  onChange={(e) => setDept(e.target.value)}
                  disabled={loading}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Theme */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Brand & Theme" />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Primary Color (hex)"
                    value={primary}
                    onChange={(e) => setPrimary(e.target.value)}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Accent Color (hex)"
                    value={accent}
                    onChange={(e) => setAccent(e.target.value)}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Header Text"
                    value={headerText}
                    onChange={(e) => setHeaderText(e.target.value)}
                    disabled={loading}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={darkModeDefault}
                        onChange={(e) => setDarkModeDefault(e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="Dark mode by default"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Features */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Features" />
            <Divider />
            <CardContent>
              <Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={competencyApproval}
                      onChange={(e) => setCompetencyApproval(e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label="Competency Approval"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={returnedEdit}
                      onChange={(e) => setReturnedEdit(e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label="Allow Returned Visit Editing (Intern)"
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Modules JSON */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Module Configuration (JSON)" />
            <Divider />
            <CardContent>
              <TextField
                label="Modules JSON"
                value={modulesText}
                onChange={(e) => setModulesText(e.target.value)}
                placeholder={`{
  "citizen": { "enabled": true, "version": 1 },
  "approvals": { "enabled": true },
  "telemedicine": { "enabled": false }
}`}
                fullWidth
                multiline
                minRows={10}
                disabled={loading}
                error={!modulesValid}
                helperText={modulesValid ? 'Valid JSON' : 'Invalid JSON'}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create Admin dialog */}
      <Dialog open={adminOpen} onClose={() => !adminBusy && setAdminOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create First Admin</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Creates an Admin user in this portal for operational setup.
          </Typography>
          <Stack spacing={2}>
            <TextField label="Name" value={adminName} onChange={(e) => setAdminName(e.target.value)} />
            <TextField label="Email" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} />
            <TextField label="Mobile" value={adminMobile} onChange={(e) => setAdminMobile(e.target.value)} />
            <TextField label="Password" type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminOpen(false)} disabled={adminBusy}>Cancel</Button>
          <Button onClick={handleCreateAdmin} variant="contained" disabled={adminBusy || !paramSlug}>
            {adminBusy ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone dashboards dialog */}
      <Dialog open={cloneOpen} onClose={() => !cloneBusy && setCloneOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Clone / Seed Portal Configuration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Provide a source portal slug to clone dashboard configs. Leave blank to seed defaults.
          </Typography>
          <TextField
            label="From Slug (optional)"
            value={fromSlug}
            onChange={(e) => setFromSlug(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneOpen(false)} disabled={cloneBusy}>Cancel</Button>
          <Button onClick={handleClone} variant="contained" disabled={cloneBusy || !paramSlug}>
            {cloneBusy ? 'Cloning…' : 'Run'}
          </Button>
        </DialogActions>
      </Dialog>
      <OwnerBrandFooter />
    </Box>
  );
}
